// Copyright (c) 2026 Broadcom. All rights reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc. and/or its subsidiaries.
//
// CDA API endpoint registry.
//
// CRA's dev proxy only forwards XHR/fetch requests (Accept != text/html).
// Full browser navigations (window.location.href) bypass the proxy and go
// directly to the CRA dev server, which returns index.html.
//
// Rule:
//   - XHR endpoints  → relative paths (/api/...) — proxied via CRA or nginx
//   - Navigation endpoints (LOGIN) → full backend URL so the browser goes
//     directly to the backend, bypassing nginx (required for OIDC PKCE session
//     cookie continuity and for port-forward dev where OIDC_REDIRECT_URI
//     points to localhost:3001 directly).
//
// API_BASE resolution order:
//   1. window.__ENV__.REACT_APP_API_BASE — injected at container start by
//      docker-entrypoint.sh (allows runtime override without rebuilding the image)
//   2. process.env.REACT_APP_API_BASE   — baked in at CRA build time (local npm start)
//   3. ''                               — empty: relative paths, nginx proxies all /api/*
//
// The runtime value is sanitized to only allow absolute http(s):// URLs or an
// empty string — this prevents javascript: / data: URI injection if the injected
// value were ever tampered with.
function sanitizeApiBase(raw) {
  if (!raw) return '';
  return /^https?:\/\//.test(raw) ? raw : '';
}

const API_BASE = sanitizeApiBase(
  (typeof window !== 'undefined' && window.__ENV__?.REACT_APP_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  ''
);
const AUTH_BASE = '/api/auth';

export const API_ENDPOINTS = {
  /** GET — initiate AuthHub OIDC login (full-page navigation → direct to backend) */
  LOGIN:        () => `${API_BASE}${AUTH_BASE}/login`,
  /**
   * GET — initiate silent AuthHub OIDC login with prompt=none (full-page navigation).
   * Used on portal-integrated environments (external stage/prod) to check whether the
   * user already has an AuthHub SSO session from the support portal. AuthHub returns an
   * auth code silently if a session exists, or redirects to /login?silent_failed=true
   * if no session is found — no login UI is shown either way.
   */
  SILENT_LOGIN: () => `${API_BASE}${AUTH_BASE}/login?silent=true`,
  /** POST — username/password form login (XHR — use relative, proxied) */
  LOGIN_POST:   () => `${AUTH_BASE}/login`,
  /** GET — destroy session (full-page navigation via window.location.href) */
  LOGOUT:       () => `${AUTH_BASE}/logout`,
  /** GET — current authenticated user profile (XHR) */
  ME:           () => `${AUTH_BASE}/me`,
  /** GET — backend dev config (XHR) */
  DEV_CONFIG:   () => `${AUTH_BASE}/dev-config`,
  /** GET — health check */
  HEALTH:       () => '/api/health',
};

/**
 * Fetch wrapper that always includes credentials (session cookie).
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function authFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
}
